<template>
    <div>
        <!-- Big Public API info box -->
        <v-card
            class="public-api-box pa-5 mb-5"
            elevation="2"
            color="primary"
            dark
        >
            <v-row align="center" no-gutters>
                <v-col cols="auto" class="pr-4">
                    <v-icon size="48">mdi-api</v-icon>
                </v-col>
                <v-col>
                    <div class="text-h6 font-weight-bold">
                        {{ translations.publicApiTitle[lang] }}
                    </div>
                    <div class="public-api-desc">
                        {{ translations.publicApiDescription[lang] }}
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <!-- Whitelist card -->
        <b-standard-card
            :title="translations.whitelistTitle[lang]"
            :loading="loading"
            hideDefaultFooter
            class="whitelist-card"
        >
            <v-row class="mb-3" no-gutters>
                <v-col cols="12" class="d-flex justify-end">
                    <v-btn
                        color="secondary"
                        class="text-capitalize px-5"
                        depressed
                        @click="openCreateDialog"
                    >
                        <v-icon left>mdi-plus</v-icon>
                        {{ translations.addIp[lang] }}
                    </v-btn>
                </v-col>
            </v-row>

            <v-data-table
                :headers="headers"
                :items="ipList"
                class="b-outlined rounded-0"
                disable-pagination
                hide-default-footer
                :no-data-text="translations.empty[lang]"
            >
                <template v-slot:item.title="{ item }">
                    <span class="font-weight-medium">{{ item.title }}</span>
                </template>
                <template v-slot:item.ip="{ item }">
                    <code class="ip-code">{{ item.ip }}</code>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn
                            icon
                            small
                            color="secondary"
                            @click="openEditDialog(item)"
                        >
                            <v-icon size="20">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            color="error"
                            @click="confirmDelete(item)"
                        >
                            <v-icon size="20">mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </b-standard-card>

        <!-- Add/Edit dialog -->
        <v-dialog v-model="dialog" max-width="500" persistent>
            <v-card>
                <v-card-title class="text-h6">
                    {{
                        editing
                            ? translations.edit[lang]
                            : translations.addIp[lang]
                    }}
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <b-text-field
                                v-model="form.title"
                                :label="translations.title[lang]"
                                :placeholder="translations.titlePlaceholder[lang]"
                                :error-messages="errors.title"
                            ></b-text-field>
                        </v-col>
                        <v-col cols="12">
                            <b-text-field
                                v-model="form.ip"
                                :label="translations.ip[lang]"
                                :placeholder="translations.ipPlaceholder[lang]"
                                :error-messages="errors.ip"
                                @blur="validateIp"
                            ></b-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        class="text-capitalize"
                        @click="closeDialog"
                    >
                        {{ translations.cancel[lang] }}
                    </v-btn>
                    <v-btn
                        color="secondary"
                        class="text-capitalize px-5"
                        depressed
                        :loading="saving"
                        @click="save"
                    >
                        {{ translations.save[lang] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import Developers from "@/models/general/settings/Developers";
import translations from "@/utils/translations/general/settings/developers/ipWhitelist";

const IPV4_RE =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export default {
    name: "ApiKeyWhitelist",

    mixins: [translations],

    data() {
        return {
            ipList: [],
            loading: false,
            saving: false,
            dialog: false,
            editing: false,
            editingId: null,
            form: {
                title: "",
                ip: ""
            },
            errors: {
                title: "",
                ip: ""
            }
        };
    },

    computed: {
        ...mapState(["superUser", "permissions", "storePermissions"]),

        lang() {
            return this.$route.params.lang;
        },

        headers() {
            return [
                {
                    text: this.translations.title[this.lang],
                    value: "title"
                },
                {
                    text: this.translations.ip[this.lang],
                    value: "ip"
                },
                {
                    text: this.translations.actions[this.lang],
                    value: "actions",
                    align: "end",
                    sortable: false
                }
            ];
        },

        canWrite() {
            return (
                this.superUser ||
                (this.permissions?.developers?.api_keys?.update &&
                    this.storePermissions?.developers?.api_keys?.update)
            );
        }
    },

    methods: {
        ...mapMutations(["setNotification", "setLoading"]),

        async getIpWhitelist() {
            this.loading = true;
            try {
                const { data } = await Developers.getIpWhitelist();
                this.ipList = data?.data?.whitelist_ips || [];
            } catch (ex) {
                this.notify(
                    "error",
                    ex.response?.data?.message ||
                        this.translations.error[this.lang]
                );
            } finally {
                this.loading = false;
            }
        },

        openCreateDialog() {
            this.editing = false;
            this.editingId = null;
            this.form = { title: "", ip: "" };
            this.errors = { title: "", ip: "" };
            this.dialog = true;
        },

        openEditDialog(item) {
            this.editing = true;
            this.editingId = item.id;
            this.form = { title: item.title, ip: item.ip };
            this.errors = { title: "", ip: "" };
            this.dialog = true;
        },

        closeDialog() {
            this.dialog = false;
            this.editing = false;
            this.editingId = null;
            this.form = { title: "", ip: "" };
            this.errors = { title: "", ip: "" };
        },

        validateIp() {
            if (!this.form.ip) {
                this.errors.ip = this.translations.ipRequired?.[this.lang] || "";
                return false;
            }
            if (!IPV4_RE.test(this.form.ip.trim())) {
                this.errors.ip = this.translations.invalidIp[this.lang];
                return false;
            }
            this.errors.ip = "";
            return true;
        },

        validateForm() {
            let valid = true;
            this.errors = { title: "", ip: "" };
            if (!this.form.title || !this.form.title.trim()) {
                this.errors.title = this.translations.titleRequired[this.lang];
                valid = false;
            }
            if (!this.validateIp()) {
                valid = false;
            }
            return valid;
        },

        async save() {
            if (!this.validateForm()) return;
            this.saving = true;
            try {
                const body = {
                    title: this.form.title.trim(),
                    ip: this.form.ip.trim()
                };
                if (this.editing) {
                    await Developers.updateIpWhitelist(this.editingId, body);
                } else {
                    await Developers.createIpWhitelist(body);
                }
                this.notify("success", this.translations.success[this.lang]);
                this.closeDialog();
                await this.getIpWhitelist();
            } catch (ex) {
                this.notify(
                    "error",
                    ex.response?.data?.message ||
                        this.translations.error[this.lang]
                );
            } finally {
                this.saving = false;
            }
        },

        async confirmDelete(item) {
            if (!window.confirm(this.translations.confirmDelete[this.lang])) {
                return;
            }
            try {
                await Developers.deleteIpWhitelist(item.id);
                this.notify("success", this.translations.deleted[this.lang]);
                await this.getIpWhitelist();
            } catch (ex) {
                this.notify(
                    "error",
                    ex.response?.data?.message ||
                        this.translations.error[this.lang]
                );
            }
        },

        notify(type, text) {
            this.setNotification({ show: true, type, text });
        }
    },

    mounted() {
        this.getIpWhitelist();
    }
};
</script>

<style scoped>
.public-api-box {
    border-radius: 12px;
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
}
.public-api-desc {
    opacity: 0.92;
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 720px;
}
.ip-code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.9rem;
}
</style>
